import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  Typography,
} from "@mui/joy";
import { Link } from "react-router-dom";
import {
  proposalDealsApi,
  dealError,
  type Deal,
  type RepairLibraryTask,
} from "../../lib/proposalDealsApi";

const blankTask = (): RepairLibraryTask => ({
  id: crypto.randomUUID(),
  title: "",
  location: "",
  issue: "",
  system: "",
  solution: "",
  quantity: "1",
  unit: "item",
  unit_price: "0.00",
});

export function RepairTaskLibrary({
  manage = false,
  deal,
  refresh,
}: {
  manage?: boolean;
  deal?: Deal;
  refresh?: () => Promise<void>;
}) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["proposal-deal-settings"],
    queryFn: proposalDealsApi.settings,
  });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<RepairLibraryTask | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState("");
  const tasks = query.data?.repair_library || [];
  const canManage = manage && query.data?.can_manage;
  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError("");
    setAdded("");
    try {
      await fn();
    } catch (e) {
      setError(dealError(e));
    } finally {
      setBusy(false);
    }
  }
  async function save(tasks: RepairLibraryTask[]) {
    await proposalDealsApi.saveSettings({ repair_library: tasks });
    await client.invalidateQueries({ queryKey: ["proposal-deal-settings"] });
    setEditing(null);
  }
  const contents = (
    <Stack spacing={2}>
      <Typography level="h4">Repair task library</Typography>
      <Typography level="body-sm">
        Reusable work descriptions and default prices. Add a copy to a proposal,
        then customize the location, quantity, and price for that customer.
      </Typography>
      {query.isError && (
        <Alert
          color="danger"
          endDecorator={
            <Button onClick={() => void query.refetch()}>Retry</Button>
          }
        >
          Could not load the library.
        </Alert>
      )}
      {error && <Alert color="danger">{error}</Alert>}
      {added && (
        <Alert color="success">
          Added {added}. You can edit it in Scope & pricing.
        </Alert>
      )}
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Input
          placeholder="Search repair tasks…"
          aria-label="Search repair tasks"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1 }}
        />
        {canManage && (
          <Button disabled={busy} onClick={() => setEditing(blankTask())}>
            New repair task
          </Button>
        )}
      </Stack>
      {query.isPending && <Typography>Loading library…</Typography>}
      {query.data && !tasks.length && (
        <Typography>
          No saved tasks yet.{" "}
          {manage ? (
            "Add your first common repair task above."
          ) : (
            <Link to="/crm/proposals/settings">
              An Org Admin can add tasks in Proposal Settings → Insurance &
              Repair.
            </Link>
          )}
        </Typography>
      )}
      {tasks
        .filter((t) =>
          `${t.title} ${t.solution} ${t.system}`
            .toLowerCase()
            .includes(search.toLowerCase()),
        )
        .map((task) => (
          <Card key={task.id}>
            <Typography level="title-md">{task.title}</Typography>
            <Typography level="body-sm" sx={{ whiteSpace: "pre-wrap" }}>
              {task.solution}
            </Typography>
            <Typography level="body-sm">
              {Number(task.quantity)} {task.unit} ×{" "}
              {Number(task.unit_price).toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Typography>
            <Stack direction="row" gap={1}>
              {canManage && (
                <>
                  <Button
                    variant="outlined"
                    disabled={busy}
                    onClick={() => setEditing(task)}
                  >
                    Edit
                  </Button>
                  <Button
                    color="danger"
                    variant="plain"
                    disabled={busy}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Remove “${task.title}” from the library? Existing proposal items stay unchanged.`,
                        )
                      )
                        void run(() =>
                          save(tasks.filter((t) => t.id !== task.id)),
                        );
                    }}
                  >
                    Remove
                  </Button>
                </>
              )}
              {deal && (
                <Button
                  disabled={deal.scope_locked || busy}
                  onClick={() =>
                    void run(async () => {
                      const fields = { ...task };
                      await proposalDealsApi.action(deal.id, "repair-items", {
                        ...fields,
                        revision: deal.revision,
                      });
                      await refresh?.();
                      setAdded(task.title);
                    })
                  }
                >
                  Add to proposal
                </Button>
              )}
            </Stack>
          </Card>
        ))}
    </Stack>
  );
  return (
    <div>
      {manage ? (
        <Card sx={{ p: 3 }}>{contents}</Card>
      ) : (
        <>
          <Button variant="outlined" onClick={() => setOpen(true)}>
            Add from library
          </Button>
          <Modal open={open} onClose={() => !busy && setOpen(false)}>
            <ModalDialog
              sx={{
                width: 700,
                maxWidth: "calc(100vw - 24px)",
                maxHeight: "90dvh",
                overflow: "auto",
              }}
            >
              <ModalClose />
              {contents}
            </ModalDialog>
          </Modal>
        </>
      )}
      <Modal open={!!editing} onClose={() => !busy && setEditing(null)}>
        <ModalDialog
          className="deal-workspace"
          sx={{
            width: 640,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: "90dvh",
            overflow: "auto",
          }}
        >
          <ModalClose />
          <Typography level="h4">
            {tasks.some((t) => t.id === editing?.id)
              ? "Edit repair task"
              : "New repair task"}
          </Typography>
          {error && <Alert color="danger">{error}</Alert>}
          {editing && (
            <form
              className="deal-form"
              onSubmit={(e) => {
                e.preventDefault();
                const values = Object.fromEntries(
                  new FormData(e.currentTarget),
                ) as Record<string, string>;
                const task = { ...editing, ...values };
                void run(() =>
                  save(
                    tasks.some((t) => t.id === task.id)
                      ? tasks.map((t) => (t.id === task.id ? task : t))
                      : [...tasks, task],
                  ),
                );
              }}
            >
              <fieldset disabled={busy}>
                <label>
                  Task name
                  <input
                    name="title"
                    defaultValue={editing.title}
                    required
                    maxLength={255}
                  />
                </label>
                <label>
                  Included work
                  <textarea
                    name="solution"
                    defaultValue={editing.solution}
                    required
                    rows={3}
                  />
                </label>
                <label>
                  Typical issue (optional)
                  <textarea
                    name="issue"
                    defaultValue={editing.issue}
                    rows={2}
                  />
                </label>
                <label>
                  System (optional)
                  <input
                    name="system"
                    defaultValue={editing.system}
                    maxLength={255}
                  />
                </label>
                <label>
                  Default location (optional)
                  <input
                    name="location"
                    defaultValue={editing.location}
                    maxLength={500}
                  />
                </label>
                <div className="deal-fields">
                  <label>
                    Quantity
                    <input
                      name="quantity"
                      type="number"
                      step="0.001"
                      min="0.001"
                      defaultValue={editing.quantity}
                      required
                    />
                  </label>
                  <label>
                    Unit
                    <input
                      name="unit"
                      defaultValue={editing.unit}
                      required
                      maxLength={40}
                    />
                  </label>
                  <label>
                    Unit price ($)
                    <input
                      name="unit_price"
                      type="number"
                      min="0"
                      step="0.01"
                      defaultValue={editing.unit_price}
                      required
                    />
                  </label>
                </div>
                <Button type="submit" loading={busy}>
                  Save repair task
                </Button>
              </fieldset>
            </form>
          )}
        </ModalDialog>
      </Modal>
    </div>
  );
}
